/** Searchable case label for inbound mail waiting to be opened. */
export const UNREAD_EMAIL_TAG_KEY = "unread_email";

const UNREAD_EMAIL_TAG_SLUGS = new Set([
  "unread_email",
  "unread_mail",
  "unread-email",
  "ulest_e_post",
  "ulest-e-post",
]);

const UNREAD_EMAIL_TAG_NAMES = new Set([
  "ulest svar fra kunde",
  "ulest e-post",
  "ulest epost",
  "unread reply from customer",
  "unread email",
  "unread e-mail",
  "oläst svar från kund",
  "olast svar fran kund",
  "oläst e-post",
  "olast e-post",
]);

/** API titles for Inbound::CaseEmailItems card (`inbound_email.card_item_title`). */
const INBOUND_RECEIVED_TITLES = new Set([
  "e-post mottatt",
  "email received",
  "e-post mottagen",
  "e-post motteken",
]);

const sessionClearedCaseIds = new Set();

export function rememberUnreadMailCleared(caseId) {
  if (caseId == null || caseId === "") return;
  sessionClearedCaseIds.add(String(caseId));
}

export function resetUnreadMailClearedForTests() {
  sessionClearedCaseIds.clear();
}

export function wasUnreadMailCleared(caseId) {
  if (caseId == null || caseId === "") return false;
  return sessionClearedCaseIds.has(String(caseId));
}

function normalizeTagToken(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ");
}

export function isUnreadEmailTag(tag) {
  if (!tag || typeof tag !== "object") return false;
  const slug = String(tag.slug || tag.key || tag.system_key || tag.code || "")
    .trim()
    .toLowerCase();
  if (slug && UNREAD_EMAIL_TAG_SLUGS.has(slug)) return true;
  const name = normalizeTagToken(tag.name);
  return Boolean(name) && UNREAD_EMAIL_TAG_NAMES.has(name);
}

export function unreadEmailTagLabel(tag, t) {
  if (isUnreadEmailTag(tag) && typeof t === "function") {
    return t("case.unread_mail.tag");
  }
  return tag?.name || "";
}

export function tagsIncludeUnreadEmail(caseItem) {
  const tags = Array.isArray(caseItem?.tag_definitions) ? caseItem.tag_definitions : [];
  return tags.some(isUnreadEmailTag);
}

export function caseHasUnreadEmailTag(caseItem) {
  if (wasUnreadMailCleared(caseItem?.id)) return false;
  return tagsIncludeUnreadEmail(caseItem);
}

export function stripUnreadEmailTags(caseItem) {
  if (!caseItem || typeof caseItem !== "object") return caseItem;
  const tags = Array.isArray(caseItem.tag_definitions) ? caseItem.tag_definitions : [];
  return {
    ...caseItem,
    tag_definitions: tags.filter((tag) => !isUnreadEmailTag(tag)),
  };
}

export function visibleCaseTagDefinitions(caseItem) {
  const tags = Array.isArray(caseItem?.tag_definitions) ? caseItem.tag_definitions : [];
  if (!wasUnreadMailCleared(caseItem?.id)) return tags;
  return tags.filter((tag) => !isUnreadEmailTag(tag));
}

function truthyFlag(value) {
  return value === true || value === "true" || value === 1;
}

function falseyFlag(value) {
  return value === false || value === "false" || value === 0;
}

export function isEmailSummaryItem(item) {
  return String(item?.action || "").toUpperCase() === "EMAIL_SUMMARY";
}

export function isInboundReceivedCard(item) {
  if (!item) return false;
  const title = normalizeTagToken(item.title);
  if (title && INBOUND_RECEIVED_TITLES.has(title)) return true;
  if (item.module !== "customer_communication") return false;
  const comm = item.module_object || {};
  const direction = String(comm.direction || item.direction || "").toLowerCase();
  if (direction === "outbound") return false;
  if (direction === "inbound") return true;
  if (comm.channel && comm.channel !== "email") return false;
  return false;
}

/** @deprecated use isInboundMailTimelineItem */
export function isInboundCustomerMailItem(item) {
  return isInboundReceivedCard(item);
}

export function isInboundMailTimelineItem(item) {
  return isEmailSummaryItem(item) || isInboundReceivedCard(item);
}

function itemExplicitlyUnread(item) {
  if (!item) return false;
  if (falseyFlag(item.unread) && !truthyFlag(item.unread_inbound_mail) && !truthyFlag(item.unread_mail)) {
    return false;
  }
  return (
    truthyFlag(item.unread) ||
    truthyFlag(item.unread_inbound_mail) ||
    truthyFlag(item.unread_mail) ||
    truthyFlag(item.module_object?.unread)
  );
}

function sortByCreatedAt(items) {
  return items.slice().sort((a, b) => new Date(a?.created_at || 0) - new Date(b?.created_at || 0));
}

/** Newest inbound received card (EMAIL_SUMMARY is hidden in the feed). */
export function latestInboundMailPairIds(items) {
  const list = sortByCreatedAt(Array.isArray(items) ? items : []);
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const item = list[i];
    if (isInboundReceivedCard(item) && item.id != null) return [item.id];
  }
  return [];
}

export function isUnreadInboundMailItem(item, { caseHasUnreadTag, openedWithUnread } = {}) {
  if (!isInboundReceivedCard(item)) return false;
  if (itemExplicitlyUnread(item)) return true;
  return Boolean(caseHasUnreadTag || openedWithUnread);
}

/**
 * Ids to highlight as the new inbound mail event.
 * Glows the `customer_communication` «E-post mottatt» card (not the hidden EMAIL_SUMMARY).
 */
export function unreadInboundMailItemIds(items, { caseHasUnreadTag, openedWithUnread } = {}) {
  const list = Array.isArray(items) ? items : [];
  const flagged = list.some((item) => itemExplicitlyUnread(item) && isInboundMailTimelineItem(item));
  if (!flagged && !caseHasUnreadTag && !openedWithUnread) return [];
  return latestInboundMailPairIds(list);
}

export function caseLastItemLooksUnread(caseItem) {
  if (caseHasUnreadEmailTag(caseItem)) return true;
  return isUnreadInboundMailItem(caseItem?.last_item, {
    caseHasUnreadTag: caseHasUnreadEmailTag(caseItem),
  });
}
